import { useEffect, useMemo, useState } from 'react';
import { Modal, Button, SimpleGrid, Select, Group, Stack, NumberInput } from '@mantine/core';
import { useForm } from '@mantine/form';
import TextInput from 'app/views/components/inputs/TextInput';
import { saveSubTradeFee } from 'app/state/ducks/sub-trades/thunks';
import { useDispatch } from 'react-redux';
import { currencyOptions, paidToOptions } from 'app/models/dropdown-options';
import { SubTradeFeeParams } from 'app/models/sub-trade-params';
import { RefinedSubTrade } from 'app/models/traded-facilities-params';
import { ErrorNotification, SuccessNotification } from 'app/views/components/notifications/notification';
import { ReactComponent as CloseIcon } from 'app/public/DrawerIcons/close-icon.svg'

interface ModalAddFeeProps {
    fee?: SubTradeFeeParams | null;
    opened: boolean;
    onClose: () => void;
    subTrade: RefinedSubTrade | any;
    /** Fired after a successful save so the parent can refetch FM data. */
    onSaved?: () => void;
}

function ModalAddFee({ fee, opened, onClose, subTrade, onSaved }: ModalAddFeeProps) {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(false)
    const form = useForm({
        initialValues: {
            feeType: fee?.feeType || '',
            currency: fee?.currency || '',
            feeAmount: fee?.feeAmount || 0,
            paidTo: fee?.paidTo || '',
            notes: fee?.notes || '',
        },
        validate: {
            feeType: (value) => (value.trim().length === 0 ? 'Type is required' : null),
            currency: (value) => (value.trim().length === 0 ? 'Currency is required' : null),
            feeAmount: (value) => (!value ? 'Amount is required' : null),
            paidTo: (value) => (value.trim().length === 0 ? 'Paid To is required' : null),
        },
    });

    // Update form values when fee prop changes (for editing)
    useEffect(() => {
        form.setValues({
            feeType: fee?.feeType || '',
            currency: fee?.currency || '',
            feeAmount: Number(fee?.feeAmount) || 0,
            paidTo: fee?.paidTo || '',
            notes: fee?.notes || '',
        });
    }, [fee, opened]);

    const handleSubmit = async (values: typeof form.values) => {
        setIsLoading(true);
        try {
            const payload = fee && fee.id ? { ...values, id: fee.id } : values;
            const data = !fee
                ? { newFee: payload, subTradeKey: { id: subTrade.id, admin: subTrade.agencyAdmin } }
                : { oldFeeType: fee.feeType, newFee: payload, subTradeKey: { id: subTrade.id, admin: subTrade.agencyAdmin } };

            const response: any = fee
                ? await dispatch(saveSubTradeFee(subTrade, data, fee))
                : await dispatch(saveSubTradeFee(subTrade, data));

            if (response?.success) {
                SuccessNotification({ title: 'Saved Sub Trade Fee' });
                onSaved?.();
            } else {
                ErrorNotification({
                    title: 'Sub Trade Fee Failed to Save',
                    message: response.payload ?? 'Check data and try again',
                });
            }
        } catch {
            ErrorNotification({
                title: 'Sub Trade Fee Failed to Save',
                message: 'Check data and try again',
            });
        } finally {
            setIsLoading(false);
            onClose();
        }
    };

    const uniqueCurrencies = useMemo(() => {
        if (!subTrade || !subTrade.tradedFacilities) return [];
        return [
            ...new Set(
                subTrade.tradedFacilities.flatMap((facility: { facilityDetails?: { currencies: string; } | null; }) =>
                    facility.facilityDetails?.currencies ?? []
                )
            )
        ];
    }, [subTrade]);

    return (
        <Modal opened={opened} onClose={onClose} centered withCloseButton={false} size="70%">
            <Stack w="100%">
                <div className="add-modal-fee">
                    <div className="add-modal-fee-title-header">
                        <div className="add-modal-fee-title">
                            <div className="add-modal-fee-title-frame">
                                <div className="add-modal-fee-title-text">
                                    {fee ? 'Edit Fee' : 'Add Fee'}
                                </div>
                            </div>
                        </div>
                        <div className="add-modal-fee-header-actions">
                            <div className="add-modal-fee-title-actions-expand">
                                <Button
                                    onClick={onClose}
                                    variant="subtle"
                                    style={{
                                        padding: 0,
                                        background: 'transparent',
                                        height: 30,
                                        width: 30,
                                        minWidth: 'auto',
                                    }}
                                    aria-label="Close modal"
                                >
                                    <CloseIcon style={{ width: '30px' }} />
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="content" style={{ width: '100%' }}>
                        <form onSubmit={form.onSubmit(handleSubmit)}>
                            <div className="create-new">
                                <div>
                                    <TextInput
                                        label="Type"
                                        placeholder="Enter Type"
                                        {...form.getInputProps('feeType')} />
                                </div>
                                <SimpleGrid cols={3} spacing="md" style={{ marginTop: '10px' }}>
                                    <Select
                                        label="Currency"
                                        placeholder="Select Currency"
                                        data={currencyOptions.filter(option => uniqueCurrencies.includes(option.value))}
                                        searchable
                                        {...form.getInputProps('currency')}
                                        styles={{ label: { fontSize: '1em' } }} />
                                    <NumberInput
                                        label="Amount"
                                        placeholder="Enter amount"
                                        min={0}
                                        defaultValue={0}
                                        parser={value => value.replace(/\$\s?|(,*)/g, '')}
                                        formatter={value =>
                                            !Number.isNaN(parseFloat(value))
                                                ? ` ${value}`.replace(
                                                    /\B(?<!\.\d*)(?=(\d{3})+(?!\d))/g,
                                                    ','
                                                )
                                                : ' '
                                        }
                                        {...form.getInputProps(`feeAmount`)}
                                        sx={{
                                            label: {
                                                fontFamily: 'Plus Jakarta Sans',
                                                color: '#111928',
                                                fontSize: '1em',
                                            },
                                            input: {
                                                backgroundColor: '#F9FAFB',
                                                height: '3em',
                                                borderRadius: '0.7em',
                                                color: '#6B7280',
                                                borderWidth: '1px',
                                                borderColor: '#D1D5DB',
                                                fontSize: '1em',
                                                fontFamily: 'Plus Jakarta Sans',
                                            },
                                        }} />
                                    <Select
                                        label="Paid To"
                                        placeholder="Enter Recipient"
                                        data={paidToOptions}
                                        {...form.getInputProps('paidTo')}
                                        styles={{ label: { fontSize: '1em' } }} />
                                </SimpleGrid>
                                <TextInput
                                    label="Notes"
                                    placeholder="Add notes"
                                    mt="md"
                                    {...form.getInputProps('notes')} />
                                <Group position="right" mt="md">
                                    <Button className="add-modal-fee-cancel-button" onClick={onClose}>
                                        <div className="add-modal-fee-cancel-button-text">Cancel</div>
                                    </Button>
                                    <Button className="add-modal-fee-save-button" type="submit" disabled={isLoading || !form.isValid()}>
                                        <div className="add-modal-fee-save-button-text">Save</div>
                                    </Button>
                                </Group>
                            </div>
                        </form>
                    </div>
                </div>
            </Stack>
        </Modal>
    );
}

export default ModalAddFee;